/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag React.Fragment
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { css as compiledCss } from '@compiled/react';
import { ErrorBoundary } from 'react-error-boundary';
import { IntlProvider } from 'react-intl';
import { useIntl } from 'react-intl-next';

import Mention from '@atlaskit/mention/mention';
import { cssMap, jsx } from '@atlaskit/css';
import { useProvider } from '@atlaskit/editor-common/provider-factory';
import Heading from '@atlaskit/heading/heading';
import ProjectIcon from '@atlaskit/icon/core/project';
import UndoIcon from '@atlaskit/icon/core/undo';
import TeamworkGraphIcon from '@atlaskit/icon-lab/core/teamwork-graph';
import IconTile from '@atlaskit/icon/icon-tile';
import Lozenge from '@atlaskit/lozenge/lozenge';
import { Popup } from '@atlaskit/popup/popup';
import { Box, Inline, Pressable, Stack, Text } from '@atlaskit/primitives/compiled';
import type ProfileClient from '@atlaskit/profilecard/profile-card-client';
import ProfileCardTrigger from '@atlaskit/profilecard/profile-card-trigger';
import { token } from '@atlaskit/tokens';
import { AtlassianRelayEnvironmentProvider } from '@atlassian/relay-environment-provider';
import { LinkList } from '@atlassian/smart-knowledge-cards/link-list';

import {
	type ProfileTriggerRenderer,
	useProfileTrigger,
} from '../../controllers/profile-trigger/ProfileTriggerContext';

import messages from './inferredProjectCardMessages';
import type { InferredProjectPerson } from './parseInferredProjectContext';
import { useInferredProjectContext } from './useInferredProjectContext';

const COMPACT_CONTRIBUTOR_LIMIT = 4;
const HOVER_CLOSE_DELAY_MS = 150;

const projectTitleButtonStyles = compiledCss({
	backgroundColor: token('color.background.neutral'),
	borderRadius: token('radius.small'),
	boxDecorationBreak: 'clone',
	color: token('color.text'),
	cursor: 'pointer',
	display: 'inline',
	font: token('font.body'),
	overflowWrap: 'break-word',
	paddingBlock: '3px',
	paddingInlineEnd: '2px',
	paddingInlineStart: '2px',
	WebkitBoxDecorationBreak: 'clone',
	whiteSpace: 'normal',
	'&:focus-visible': {
		outlineColor: token('color.border.focused'),
		outlineOffset: token('space.025'),
		outlineStyle: 'solid',
		outlineWidth: token('border.width'),
	},
	'&:hover': {
		backgroundColor: token('color.background.neutral.hovered'),
	},
});

const projectIconStyles = compiledCss({
	alignItems: 'center',
	display: 'inline-flex',
	flexShrink: 0,
	marginInlineEnd: token('space.025'),
	transform: 'translateY(-1px)',
	verticalAlign: 'middle',
});

const projectTitleStyles = compiledCss({
	minWidth: 0,
	overflowWrap: 'break-word',
	whiteSpace: 'normal',
});

const projectLabelStyles = compiledCss({
	alignItems: 'center',
	display: 'inline-flex',
	marginInlineStart: token('space.050'),
	transform: 'translateY(-1px)',
	verticalAlign: 'middle',
});

const styles = cssMap({
	card: {
		backgroundColor: token('elevation.surface.raised'),
		borderRadius: token('radius.medium'),
		maxWidth: '375px',
		minWidth: '320px',
	},
	basedOn: {
		backgroundColor: token('elevation.surface.sunken'),
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- The design requires a static gradient-and-grid treatment; its colors use design tokens.
		backgroundImage: `linear-gradient(270deg, rgba(248, 248, 248, 0) 0%, ${token('elevation.rovo.surface.overlay')} 100%), linear-gradient(${token('color.border.disabled')} 1px, transparent 1px), linear-gradient(90deg, ${token('color.border.disabled')} 1px, transparent 1px)`,
		backgroundPosition: '0 0, 6px 4px, 6px 4px',
		backgroundSize: '100% 100%, 24px 24px, 24px 24px',
		borderRadius: token('radius.medium'),
	},
	flippedIcon: {
		display: 'inline-flex',
		transform: 'rotate(180deg)',
	},
	sourceLink: {
		backgroundColor: token('elevation.surface'),
		borderRadius: token('radius.medium'),
	},
	contributors: {
		paddingBlockStart: token('space.100'),
	},
	showAllTrigger: {
		backgroundColor: 'transparent',
		paddingInline: '0',
	},
});

export type InferredProjectInlineCardProps = {
	cloudId: string;
	fallback: React.ReactNode;
	projectId: string;
	projectName: string;
};

const getAccountId = (ari: string | undefined): string | undefined => {
	const match = ari?.match(/^ari:cloud:identity::user\/(.+)$/);
	return match?.[1];
};

type ContributorChipProps = {
	cloudId: string;
	locale: string;
	person: InferredProjectPerson;
	profileClient: ProfileClient | null;
	renderProfileTrigger?: ProfileTriggerRenderer;
};

const ContributorChip = ({
	cloudId,
	locale,
	person,
	profileClient,
	renderProfileTrigger,
}: ContributorChipProps): React.ReactNode => {
	const pill = (
		<IntlProvider locale={locale}>
			<Mention
				avatarUrl={person.avatarUrl}
				id={person.ari ?? person.name}
				renderAvatarSlot={Boolean(person.avatarUrl)}
				text={`@${person.name}`}
			/>
		</IntlProvider>
	);
	const accountId = getAccountId(person.ari);

	if (!accountId) {
		return pill;
	}

	if (renderProfileTrigger) {
		return renderProfileTrigger({ userId: accountId, cloudId, children: pill });
	}

	if (profileClient) {
		return (
			<ProfileCardTrigger
				ariaLabel={person.name}
				cloudId={cloudId}
				resourceClient={profileClient}
				userId={accountId}
			>
				{pill}
			</ProfileCardTrigger>
		);
	}

	return pill;
};

const dedupeContributors = (people: readonly InferredProjectPerson[]): InferredProjectPerson[] => {
	const seen = new Set<string>();
	return people.filter((person) => {
		const key = person.ari ?? person.name;
		if (seen.has(key)) {
			return false;
		}
		seen.add(key);
		return true;
	});
};

type InferredProjectCardContentProps = Omit<InferredProjectInlineCardProps, 'fallback'>;

const InferredProjectCardInner = ({
	cloudId,
	projectId,
	projectName,
}: InferredProjectCardContentProps): React.ReactNode => {
	const { formatMessage, locale } = useIntl();
	const [isOpen, setIsOpen] = useState(false);
	const lockedByClick = useRef(false);
	const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
	const { load, ...state } = useInferredProjectContext({ cloudId, projectId });
	const [isShowingAllContributors, setIsShowingAllContributors] = useState(false);
	const profilecardProviderPromise = useProvider('profilecardProvider');
	const renderProfileTrigger = useProfileTrigger();
	const [profileClient, setProfileClient] = useState<ProfileClient | null>(null);

	useEffect(() => {
		let isMounted = true;
		profilecardProviderPromise?.then(({ resourceClient }) => {
			if (isMounted && resourceClient) {
				setProfileClient(resourceClient as ProfileClient);
			}
		});

		return () => {
			isMounted = false;
		};
	}, [profilecardProviderPromise]);

	useEffect(
		() => () => {
			if (closeTimer.current) {
				clearTimeout(closeTimer.current);
			}
		},
		[],
	);

	const open = useCallback(() => {
		if (!isOpen) {
			load();
			setIsOpen(true);
		}
	}, [isOpen, load]);
	const closeAndUnlock = useCallback(() => {
		lockedByClick.current = false;
		setIsOpen(false);
	}, []);
	const cancelScheduledClose = useCallback(() => {
		if (closeTimer.current) {
			clearTimeout(closeTimer.current);
			closeTimer.current = null;
		}
	}, []);
	const handleTriggerMouseEnter = useCallback(() => {
		cancelScheduledClose();
		open();
	}, [cancelScheduledClose, open]);
	const handleTriggerMouseLeave = useCallback(() => {
		closeTimer.current = setTimeout(() => {
			if (!lockedByClick.current) {
				closeAndUnlock();
			}
		}, HOVER_CLOSE_DELAY_MS);
	}, [closeAndUnlock]);
	const handleTriggerClick = useCallback(() => {
		if (isOpen && lockedByClick.current) {
			closeAndUnlock();
			return;
		}

		lockedByClick.current = true;
		open();
	}, [closeAndUnlock, isOpen, open]);
	const handleContentMouseEnter = useCallback(() => {
		cancelScheduledClose();
	}, [cancelScheduledClose]);
	const handleContentMouseLeave = useCallback(() => {
		if (!lockedByClick.current) {
			closeAndUnlock();
		}
	}, [closeAndUnlock]);
	const handleContentBlur = useCallback(
		(event: React.FocusEvent<HTMLElement>) => {
			if (!event.currentTarget.contains(event.relatedTarget)) {
				handleContentMouseLeave();
			}
		},
		[handleContentMouseLeave],
	);

	const context = state.status === 'success' ? state.data : undefined;
	const title = context?.title ?? projectName;
	const contributors = dedupeContributors([
		...(context?.owners ?? []),
		...(context?.collaborators ?? []),
	]);
	const hasMoreContributors = contributors.length > COMPACT_CONTRIBUTOR_LIMIT;
	const visibleContributors = isShowingAllContributors
		? contributors
		: contributors.slice(0, COMPACT_CONTRIBUTOR_LIMIT);
	const source = context?.sources[0];

	// Uses inline button semantics so long project names wrap within response text.
	return (
		<Popup
			isOpen={isOpen}
			onClose={closeAndUnlock}
			placement="bottom-start"
			shouldRenderToParent
			content={() => (
				<Box
					onBlur={handleContentBlur}
					onFocus={handleContentMouseEnter}
					onMouseEnter={handleContentMouseEnter}
					onMouseLeave={handleContentMouseLeave}
					paddingBlockEnd="space.200"
					paddingBlockStart="space.150"
					paddingInline="space.200"
					xcss={styles.card}
				>
					<Stack space="space.100">
						<Stack space="space.025">
							<Heading color="color.text" size="xsmall">
								{title}
							</Heading>
							<Text color="color.text.subtlest" size="small">
								{formatMessage(messages.inferredProject)}
							</Text>
						</Stack>
						{state.status === 'loading' ? <Text>{formatMessage(messages.loading)}</Text> : null}
						{state.status === 'error' ? <Text>{formatMessage(messages.loadError)}</Text> : null}
						{context ? (
							<Stack space="space.100">
								{source ? (
									<Box padding="space.100" xcss={styles.basedOn}>
										<Stack space="space.100">
											<Text color="color.text.subtlest" size="small">
												<span css={styles.flippedIcon}>
													<UndoIcon color={token('color.icon.subtlest')} label="" size="small" />
												</span>{' '}
												{formatMessage(messages.basedOnPrefix)}{' '}
												<TeamworkGraphIcon
													color={token('color.icon.subtlest')}
													label=""
													size="small"
												/>{' '}
												{formatMessage(messages.teamworkGraphActivityAndSource)}
											</Text>
											<Box xcss={styles.sourceLink}>
												<LinkList
													expandedMaxSources={1}
													maxSources={1}
													showExpand={false}
													size="compact"
													titles={source.title ? [source.title] : undefined}
													urls={[source.url]}
												/>
											</Box>
										</Stack>
									</Box>
								) : null}
								{context.summary ? (
									<Text color="color.text.subtle" size="medium">
										{context.summary}
									</Text>
								) : null}
								<Stack space="space.150" xcss={styles.contributors}>
									<Inline alignBlock="center" spread="space-between">
										<Text color="color.text" size="small" weight="medium">
											{formatMessage(messages.suggestedContributors)}
										</Text>
										{hasMoreContributors && !isShowingAllContributors ? (
											<Pressable
												onClick={() => setIsShowingAllContributors(true)}
												xcss={styles.showAllTrigger}
											>
												<Text color="color.text.subtle" size="small">
													{formatMessage(messages.showAllContributors)}
												</Text>
											</Pressable>
										) : null}
									</Inline>
									{visibleContributors.length ? (
										<Inline shouldWrap space="space.075">
											{visibleContributors.map((contributor) => (
												<ContributorChip
													cloudId={cloudId}
													locale={locale}
													key={contributor.ari ?? contributor.name}
													person={contributor}
													profileClient={profileClient}
													renderProfileTrigger={renderProfileTrigger}
												/>
											))}
										</Inline>
									) : (
										<Text color="color.text.subtle">
											{formatMessage(messages.noSuggestedContributors)}
										</Text>
									)}
								</Stack>
							</Stack>
						) : null}
					</Stack>
				</Box>
			)}
			trigger={(triggerProps) => (
				// eslint-disable-next-line @atlaskit/design-system/no-html-button -- The project treatment must wrap with response text, which standard buttons cannot support.
				<span
					{...triggerProps}
					aria-label={formatMessage(messages.inferredProjectTrigger, { projectName })}
					css={projectTitleButtonStyles}
					onClick={handleTriggerClick}
					onKeyDown={(event) => {
						if (
							event.key === 'Enter' ||
							event.key === ' ' ||
							event.key === 'Space' ||
							event.key === 'Spacebar' ||
							event.code === 'Space'
						) {
							event.preventDefault();
							handleTriggerClick();
						}
					}}
					onBlur={handleTriggerMouseLeave}
					onFocus={handleTriggerMouseEnter}
					onMouseEnter={handleTriggerMouseEnter}
					onMouseLeave={handleTriggerMouseLeave}
					role="button"
					tabIndex={0}
				>
					<span>
						<span css={projectIconStyles}>
							<IconTile appearance="purple" icon={ProjectIcon} label="" size="xsmall" />
						</span>
						<span css={projectTitleStyles}>{projectName}</span>
					</span>
					<span css={projectLabelStyles}>
						<Lozenge appearance="discovery">{formatMessage(messages.inferredProject)}</Lozenge>
					</span>
				</span>
			)}
		/>
	);
};

export const InferredProjectInlineCard = ({
	fallback,
	...props
}: InferredProjectInlineCardProps): React.ReactNode => (
	<ErrorBoundary fallback={<>{fallback}</>}>
		<AtlassianRelayEnvironmentProvider>
			<InferredProjectCardInner key={`${props.cloudId}:${props.projectId}`} {...props} />
		</AtlassianRelayEnvironmentProvider>
	</ErrorBoundary>
);
